import { FunctionComponent, memo, useContext, useState } from "react";
import { IProps, ITriggerPerformanceChartFilters } from "./phrase-triggers-performance-report.types";
import { useHistory, useParams } from "react-router-dom";
import { Page, PageContent, PageHeader } from "@edgetier/client-components";
import usePhraseTrigger from "hooks-for/phrase-triggers/use-phrase-trigger";
import { Button } from "@edgetier/components";
import { faEdit, faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import urljoin from "url-join";
import RoutePage from "constants/page";
import PhraseTriggersResultsTable from "./phrase-triggers-results-table";
import PhraseTriggersPerformanceChart from "./phrase-triggers-performance-chart";
import useChartDrag from "hooks-for/anomalies/use-chart-drag";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { PHRASE_TRIGGER_REPORT_PREFIX } from "./phrase-triggers-performance-report.constants";
import { PhraseTriggerContext } from "components-for/phrase-triggers/phrase-trigger-context/phrase-trigger-context";
import { trackUser } from "utilities/track-user";
import "./phrase-triggers-performance-report.scss";
/**
 * Display the performance report for a phrase trigger
 */
const PhraseTriggersPerformanceReport: FunctionComponent<IProps> = () => {
    const history = useHistory();
    const { tagId, phraseTriggerId } = useParams<{
        readonly tagId: string;
        readonly phraseTriggerId: string;
    }>();

    const { filters: globalFilters } = useContext(PhraseTriggerContext);

    // Filters used both in chart and table. Use phrase trigger filters as initial values
    const [filters, setFilters] = useState<ITriggerPerformanceChartFilters>({
        startDateTime: globalFilters.startDateTime,
        endDateTime: globalFilters.endDateTime,
    });

    // Retrieve phrase details
    const { data: phraseTrigger, isLoading: isPhraseTriggerLoading } = usePhraseTrigger(phraseTriggerId!, {
        enabled: typeof phraseTriggerId === "string",
    });

    const { filterDateRange, resetChartFilter, mouseEventCallbacks } = useChartDrag({
        defaultValues: {
            startDateTime: filters.startDateTime,
            endDateTime: filters.endDateTime,
        },
        timePeriodGroupBy: TimePeriodGroupBy.Days,
        prefixOptions: {
            prefix: PHRASE_TRIGGER_REPORT_PREFIX,
            isPrefixOptional: true,
        },
    });

    return (
        <Page className="phrase-triggers-performance-report">
            <PageHeader
                title="Trigger Performance"
                subTitle={
                    isPhraseTriggerLoading
                        ? "...loading"
                        : `${typeof phraseTrigger === "undefined" ? "Phrase Trigger" : phraseTrigger.phrase}`
                }
            >
                {typeof phraseTriggerId === "string" && typeof tagId === "string" && (
                    <div className="phrase-triggers-performance-report__actions">
                        <Button
                            outline
                            styleName="positive"
                            icon={faArrowLeft}
                            onClick={() => {
                                trackUser("Load Trigger Page");
                                history.push(urljoin(RoutePage.ManageTags, tagId));
                            }}
                        >
                            Triggers
                        </Button>
                        <Button
                            styleName="primary"
                            icon={faEdit}
                            onClick={() => {
                                trackUser("Edit Phrase Trigger");
                                history.push(
                                    urljoin(RoutePage.ManagePhraseTriggers.replace(":tagId", tagId), phraseTriggerId)
                                );
                            }}
                        >
                            Edit
                        </Button>
                    </div>
                )}
            </PageHeader>
            <PageContent>
                {typeof phraseTriggerId === "string" && typeof tagId === "string" && (
                    <>
                        <PhraseTriggersPerformanceChart
                            filters={filters}
                            updateFilters={(formFilters) => setFilters(formFilters)}
                            phraseTriggerId={phraseTriggerId}
                            mouseCallbacks={mouseEventCallbacks}
                            chartFilterRange={filterDateRange}
                            resetChartFilterRange={resetChartFilter}
                            tagId={tagId}
                        />
                        <PhraseTriggersResultsTable
                            phraseTriggerId={phraseTriggerId}
                            filters={filters}
                            chartFilterRange={filterDateRange}
                        />
                    </>
                )}
            </PageContent>
        </Page>
    );
};

export default memo(PhraseTriggersPerformanceReport);
